<script setup lang="ts">
import { debounce } from 'lodash-es';
import {
  computed,
  onMounted,
  ref,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type {
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import type { IRegistrationConfirmFields } from '@modules/registration/types';
import { useConfig } from '@modules/config/composables';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { FORM_INPUT_DEBOUNCE_DEFAULT_MS } from '@modules/forms/constants';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { LOGIN_ROUTE_NAME } from '@modules/login/constants';
import {
  REGISTRATION_DONE_ROUTE_NAME,
  TRANSLATION_GROUP_REGISTRATION,
} from '@modules/registration/constants';
import { useRegisterConfirmationMutation } from '@modules/registration/mutations';
import { useToastStore } from '@modules/toast/store';
import { isString } from '@utils/guards';

const { displayPresetToast } = useToastStore();
const { getTranslation } = useI18n();
const { config } = useConfig();

const route = useRoute();
const router = useRouter();
const token = computed(() => (isString(route.params.token) ? route.params.token : ''));

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
} = useRegisterConfirmationMutation();

const hasErrors = computed(() => !!Object.keys(errors.value ?? {}).length);

const form = ref<VFormRef>();

const schema = computed<FormSchema<IRegistrationConfirmFields>>(() => ({
  confirmation_token: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'CONFIRMATION.TOKEN').value,
    value: token.value,
    required: true,
  },
}));

function onFormChange(): void {
  form.value?.submit();
}

const debouncedChange = debounce(onFormChange, FORM_INPUT_DEBOUNCE_DEFAULT_MS);

onMounted(() => {
  if (token.value) {
    onFormChange();
  }
});

async function onSubmit(values: IRegistrationConfirmFields): Promise<void> {
  await mutateAsync(values);
  displayPresetToast('success', getTranslation(TRANSLATION_GROUP_REGISTRATION, 'CONFIRMATION.CONFIRM_EMAIL').value);

  if (config.value?.auto_accept_affiliate_request) {
    void router.replace({
      name: LOGIN_ROUTE_NAME,
    });
  } else {
    void router.replace({
      name: REGISTRATION_DONE_ROUTE_NAME,
    });
  }
}

const customErrors = computed(() => {
  if (!errors.value) {
    return undefined;
  }

  return {
    confirmation_token: Object.values(errors.value)[0],
  };
});

function onInteracted(): void {
  clearError('confirmation_token');
  clearError('email');

  debouncedChange();
}
</script>

<template>
  <div v-data-qa-auto-id="'RegistrationConfirmationPage'" class="input-max-width">
    <div class="text-center text-3xl font-light uppercase">
      {{ $getTranslation(TRANSLATION_GROUP_REGISTRATION, 'CONFIRMATION.HEADER') }}
    </div>
    <div class="my-6 text-center text-muted-color">
      {{ $getTranslation(TRANSLATION_GROUP_REGISTRATION, 'CONFIRMATION.HINT') }}
    </div>
    <VForm
      ref="form"
      :schema="schema"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_REGISTRATION, 'CONFIRMATION.NEXT')"
      :is-loading="isLoading"
      :custom-errors="customErrors"
      is-buttons-centered
      :disabled="hasErrors"
      @interacted="onInteracted"
      @submit="onSubmit"
    />
  </div>
</template>
